import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { OrganizationModel } from 'src/app/organization/@shared/organization.model';
import { fillSkeletonData } from 'src/app/@shared/app-helper';
import { transformToUpperSnakeCase } from 'src/app/@shared/utils/to-upper-snake-case';

const TRANSPARENCY_LABEL_ICON_SRC = './assets/icons/steering-wheel.svg';

@Component({
  // use onPush approach when performance is poor
  // changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'organization-preview',
  standalone: false,
  styleUrls: ['./organization-preview.component.scss'],
  templateUrl: './organization-preview.component.html',
})
export class OrganizationPreviewComponent {
  @Input() public isDisabled: boolean;

  @Input()
  public set hideTopic(value: boolean) {
    this._hideTopic = value;
    if (!this.showSkeleton) {
      this.updatePreviewText();
    }
  }

  public get hideTopic(): boolean {
    return this._hideTopic;
  }

  @Output() public openDetail: EventEmitter<OrganizationModel> = new EventEmitter<OrganizationModel>();

  public showSkeleton: boolean = true;
  public title: string = fillSkeletonData('name');
  public subheadline: string = this.getSkeletonSubheadline();
  public transparencyLabelIconSrc: string = TRANSPARENCY_LABEL_ICON_SRC;
  public showTransparencyLabelIcon: boolean = false;
  private translate = inject(TranslateService);
  private _hideTopic: boolean = false;

  // fill item with skeleton data
  // !!!IMPORTANT!!! always use a skeleton wrapper (<span class="skeleton">...</span>) around the value
  // to ensure skeleton text (single- as well as multiline) works as expected
  private _item: Partial<OrganizationModel> = {
    addressCity: fillSkeletonData('city'),
    legacyId: fillSkeletonData('legacyId'),
    name: fillSkeletonData('name'),
  };

  @Input()
  public get item(): OrganizationModel {
    return this._item as OrganizationModel;
  }

  public set item(item: OrganizationModel | null) {
    // item is initially null (skeleton)
    if (!item) {
      this.showTransparencyLabelIcon = false;

      return;
    }

    this._item = item as Partial<OrganizationModel>;
    this.updatePreviewText();
    this.setTransparencyLabelIconState();
    this.showSkeleton = false;
  }

  private getTitle(): string {
    if (!this.item) {
      return '';
    }

    if (this.item.subType === 'selfHelpAddressBa' && !this.hideTopic) {
      return this.item.topics?.[0]?.title || this.item.name || this.item.legacyId || '';
    }

    return this.item.name || this.item.legacyId || '';
  }

  private getSubheadline(): string {
    if (!this.item) {
      return '';
    }

    if (this.item.subType === 'selfHelpAddressBa') {
      return this.joinSubheadlineParts([this.item.legacyId, this.getFirstContactPerson()]);
    }

    if (this.item.subType === 'selfHelpAddressGa'
      || ['selfHelpAddressCommunity', 'selfHelpAddressShGroup'].includes(this.item.subType)) {
      const postalCodes = this.item.meetingPostalCode?.split(/\s*,\s*/).filter(Boolean) ?? [];
      const location = (this.item.meetingLocation?.trim() ?? '').replace(
        /(^|\D)(\d{4})(?=\D|$)/g,
        (match, prefix, postalCode) => postalCodes.includes(`0${postalCode}`)
          ? `${prefix}0${postalCode}`
          : match,
      );
      const locationPostalCodes = new Set(
        Array.from(location.matchAll(/(?:^|\D)(\d{4,5})(?=\D|$)/g), (match) => match[1]),
      );
      const postalCodesToDisplay = postalCodes
        .filter((postalCode) => !locationPostalCodes.has(postalCode)).join(', ');
      const meetingPlace = [postalCodesToDisplay, location].filter(Boolean).join(' ');
      const meetingPlaceText = meetingPlace
        ? `${this.translate.instant('FORM.INPUT.MEETING_LOCATION.LABEL')}: ${meetingPlace}`
        : '';

      return this.joinSubheadlineParts([
        this.item.legacyId,
        this.getTranslatedSubTypeForm(),
        this.hideTopic ? undefined : this.item.topics?.[0]?.title,
        meetingPlaceText,
      ]);
    }

    return this.joinSubheadlineParts([this.item.legacyId, this.item.addressCity]);
  }

  private getFirstContactPerson(): string {
    const firstContactPerson = this.item?.contactPersons?.[0]?.person;

    if (!firstContactPerson) {
      return '';
    }

    return [firstContactPerson.firstName, firstContactPerson.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  private getTranslatedSubTypeForm(): string {
    if (!this.item?.subTypeForm) {
      return '';
    }

    const translationKey = `FORM.INPUT.SUB_TYPE_FORM.OPTIONS.${transformToUpperSnakeCase(this.item.subTypeForm)}`;
    const translatedSubTypeForm = this.translate.instant(translationKey);

    if (translatedSubTypeForm === translationKey) {
      return this.item.subTypeForm;
    }

    return translatedSubTypeForm;
  }

  private joinSubheadlineParts(parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(' · ');
  }

  private getSkeletonSubheadline(): string {
    return this.joinSubheadlineParts([fillSkeletonData('legacyId'), fillSkeletonData('city')]);
  }

  private updatePreviewText(): void {
    this.title = this.getTitle();
    this.subheadline = this.getSubheadline();
  }

  private setTransparencyLabelIconState(): void {
    this.showTransparencyLabelIcon = !!this.item?.hasTransparencyLabel && !!this.item?.transparencyPdf;
  }
}
